import { useEffect, useRef, useState } from "react";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import "./landing.scss";

const SHOW_AIRDROP = false; // toggle to true when ready

export default function LandingPage() {
  const [mounted, setMounted] = useState(false);
  const featuredTrackRef = useRef(null);
  const featuredLogos = [
    { name: "Coingecko", src: "/featured/coingecko.svg", href: "https://www.coingecko.com/en/coins/bob-6" },
    { name: "Kizzy", src: "/featured/kizzy.svg", href: "https://kizzy.io" },
    { name: "Nad.fun", src: "/featured/nadfun.svg", href: "https://nad.fun/tokens/0x21E325B059Cd83d4037C82F0F5998Ba2dF3d7777" },
    { name: "OpenSea", src: "/featured/opensea.svg", href: "https://opensea.io/token/monad/0x21e325b059cd83d4037c82f0f5998ba2df3d7777" },
    { name: "Capricorn", src: "/featured/capricorn.svg", href: "https://app.capricorn.exchange/pools/0xe5a8f88c3db25434b6b4320a8e14348c447dd7a8" },
  ];

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let rafId = 0;

    const applyNavOffset = () => {
      rafId = 0;
      const root = document.getElementById("landing-page");
      const nav = document.querySelector("nav");
      if (!root || !(nav instanceof HTMLElement)) return;
      const navHeight = Math.ceil(nav.getBoundingClientRect().height);
      root.style.setProperty("--landing-nav-offset", `${navHeight}px`);
    };

    const onMeasure = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(applyNavOffset);
    };

    applyNavOffset();
    window.addEventListener("resize", onMeasure);
    window.addEventListener("scroll", onMeasure, { passive: true });

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onMeasure);
      window.removeEventListener("scroll", onMeasure);
    };
  }, []);

  useEffect(() => {
    const track = featuredTrackRef.current;
    if (!track) return undefined;

    let raf = 0;

    const recomputeMarquee = () => {
      const firstCycleItems = track.querySelectorAll('.landing-featured-item[data-cycle="0"]');
      if (!firstCycleItems.length) return;

      const gap = parseFloat(window.getComputedStyle(track).gap || "0") || 0;
      const totalWidth = Array.from(firstCycleItems).reduce((sum, item) => sum + item.offsetWidth, 0);
      const loopDistance = totalWidth + gap * firstCycleItems.length;
      if (loopDistance <= 0) return;

      const pxPerSecond = window.innerWidth <= 768 ? 16.64 : 19.76;
      const durationSec = loopDistance / pxPerSecond;

      track.style.setProperty("--featured-loop-distance", `${loopDistance}px`);
      track.style.setProperty("--featured-loop-duration", `${durationSec.toFixed(2)}s`);
    };

    const scheduleRecompute = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recomputeMarquee);
    };

    const imgs = Array.from(track.querySelectorAll("img"));
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", scheduleRecompute, { once: true });
    });

    scheduleRecompute();
    window.addEventListener("resize", scheduleRecompute);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", scheduleRecompute);
    };
  }, []);

  return (
    <div id="landing-page">
      {/* Blurred full-screen background using the same hero image */}
      <div
        className="landing-bg"
        aria-hidden="true"
        style={{ "--landing-bg-image": 'url("/landing/bobspace.webp")' }}
      />

      <Nav useSmoothScroll={false} />

      <main className={`landing-hero ${mounted ? "in" : ""}`}>
        <div className="landing-container landing-grid">
            {/* Left: copy */}
            <div className="landing-copy-block">
              <div className="landing-copy-inner">
                <p className="landing-kicker">Welcome to the Bobverse</p>

                <div className="landing-title-block">
                  <h1 className="landing-title">
                    <span className="bob-font bob-title">BOB</span>
                  </h1>
                  <h2 className="landing-subtitle-hero">
                    For the Bobs. By the Bobs.
                  </h2>
                </div>

                {/* Monad pill */}
                <div className="landing-monad-pill">
                  <span className="monad-label">Powered by</span>
                  <img
                    src="/monad/monad.png"
                    alt="Monad"
                    className="monad-logo"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <div className="landing-cta-row">
                  <a
                    href="https://nad.fun/tokens/0x21E325B059Cd83d4037C82F0F5998Ba2dF3d7777"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="landing-cta primary-cta"
                    aria-label="Buy BOB"
                  >
                    Bob Token
                  </a>

                  <a
                    href="/dashboard"
                    className="landing-cta secondary-cta"
                    aria-label="Open Dashboard"
                  >
                    Dashboard
                  </a>

                  {/* Airdrop CTA (hidden for now) */}
                  {SHOW_AIRDROP && (
                    <a href="/claim" className="landing-cta secondary-cta">
                      Check Airdrop
                    </a>
                  )}
                </div>

                <p className="landing-note">Bob is a community token on Monad. Grab your BOB, join the Bobverse, and ride with the community from day one.</p>
              </div>
            </div>

            {/* Right: image */}
            <div className="landing-image-block">
              <div className="landing-image-card">
                <div className="landing-image-orbit" aria-hidden="true" />
                <div className="landing-image-inner">
                  <img
                    src="/landing/bobspace.webp"
                    alt="Bob in terminator mode"
                    loading="eager"
                    decoding="async"
                  />
                  <div className="landing-image-glow" aria-hidden="true" />
                </div>
              </div>
            </div>
        </div>

        <div className="landing-container landing-featured-row" aria-label="Featured On">
          <h3 className="landing-featured-title">Featured On</h3>
          <div className="landing-featured-marquee" aria-hidden="true">
            <div className="landing-featured-track" ref={featuredTrackRef}>
              {[...featuredLogos, ...featuredLogos].map((logo, idx) => (
                <a
                  key={`${logo.name}-${idx}`}
                  href={logo.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`landing-featured-item landing-featured-item-${logo.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  data-cycle={idx < featuredLogos.length ? "0" : "1"}
                  aria-label={`View ${logo.name}`}
                >
                  <img
                    src={logo.src}
                    alt={logo.name}
                    loading={idx < featuredLogos.length ? "eager" : "lazy"}
                    decoding="async"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
